/**
 * ###################################################
 *
 * The concept of this encryptedStorage is following :
 *
 *   Only store an encrytion key in the SecureStorage and store the data encrypted with this key in another location. In Detail:
 *
 * - Generate a Radom key using expo-crypto and put the key in SecureStorage (fallback to LocalStorage on the Web).
 *
 * - AES Encrypt all data with this key. This allows us to securly put the data in an unancrypted storage location.
 *
 * - For the data storage we use on
 *
 * iOS: AsyncStorage because it allows us to turn off the iCloud Backup synchronization (see https://react-native-async-storage.github.io/async-storage/docs/advanced/backup)
 *
 *     Android: We Store the Data in Expo-File-System (because of the 6mb Size Limit of AsyncStorage on Android see https://react-native-async-storage.github.io/async-storage/docs/limits )
 *
 *     Web: localforage. We store encrypted the data directly as ArrayBuffer into indexed db, this makes the encryption even faster as there is no need to convert it to string.
 *
 *     It can be used either as persistence Layer for EasyPeasy stores (import createStorage from './encryptedStorage') or used directly as Key-Value Storage (FileSystemStorage.setItem(key,value,directory).
 *
 * ###################################################
 */

import constants from '@/config/constants';
import { logError } from '@/utils/logHandling';
import { extractSetIdFromPrefixedKey } from '@/utils/setStorageKey';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  getRandomBytes,
  digestStringAsync,
  CryptoDigestAlgorithm,
} from 'expo-crypto';
import { CryptoEncoding } from 'expo-crypto';
import { getItemAsync, setItemAsync, deleteItemAsync } from 'expo-secure-store';
import localforage from 'localforage';
import { Alert, Platform } from 'react-native';
import { IS_WEB, IS_IOS, IS_ANDROID } from '@/utils/platform';
import aesHelperMobile from './encryption/aesHelperMobile';
import { aesGcmDecrypt, aesGcmEncrypt } from './encryption/aesHelperWeb';
import { FileSystemStorage } from './file_system_storage/expoFileSystemStorage';
import {
  loadDeviceTokenDataWeb,
  saveDeviceTokenDataWeb,
} from '@/utils/secureDeviceToken';

const isAndroid8AndAbove = IS_ANDROID && (Platform.Version as number) >= 26;

const majorVersionIOS = parseInt(Platform.Version?.toString(), 10);
const isIOS13AndAbove = IS_IOS && majorVersionIOS >= 13;

let encryptionKey: null | string = null;

const HAS_SUBTLE_CRYPTO_WEB =
  window && window.crypto && window.crypto.subtle !== undefined;
const HAS_NATIVE_AES_GCM_CRYPTO =
  !constants.isRunningInExpoGo &&
  (IS_IOS || IS_ANDROID) &&
  (isAndroid8AndAbove || isIOS13AndAbove);

let encryptionKeyName: string;
if (HAS_NATIVE_AES_GCM_CRYPTO) {
  encryptionKeyName = 'EncryptionKeyNative';
} else {
  encryptionKeyName = 'y';
}

const ENCRYPTION_ENABLED = !constants.isRunningInExpoGo;
if (
  ENCRYPTION_ENABLED &&
  !HAS_NATIVE_AES_GCM_CRYPTO &&
  !HAS_SUBTLE_CRYPTO_WEB
) {
  let errorMessage;
  if (IS_WEB) {
    errorMessage =
      'Fehler: Die Verschlüsselung der Daten funktioniert in Ihrem Browser nicht. Die App wird jetzt geschlossen, um Ihre Daten zu schützen';
  } else {
    errorMessage =
      'Fehler: Die Verschlüsselung der Daten funktioniert auf Ihrem Gerät nicht. Die App wird jetzt geschlossen, um Ihre Daten zu schützen';
  }
  displayErrorAlert(errorMessage, 'Verschlüsselungsfehler');
}

function displayErrorAlert(text: string, titleMobileOnly: string): void {
  if (IS_WEB) {
    alert(text);
    throw new Error(text);
  } else {
    Alert.alert(
      titleMobileOnly,
      text,
      [
        {
          text: 'OK',
          onPress: () => {
            throw new Error(text);
          },
        },
      ],
      { cancelable: false },
    );
  }
}

export default () => {
  const noop = (...args: any[]) => null;

  const getEncryptionKey = async () => {
    if (encryptionKey == null) {
      let encryptionKeyValue;
      if (IS_WEB) {
        encryptionKeyValue = window.localStorage.getItem(encryptionKeyName);
      } else {
        encryptionKeyValue = await getItemAsync(encryptionKeyName);
      }

      if (!encryptionKeyValue) {
        encryptionKeyValue = await generateRandomEncryptionKey();
        if (IS_WEB) {
          window.localStorage.setItem(encryptionKeyName, encryptionKeyValue);
        } else {
          await setItemAsync(encryptionKeyName, encryptionKeyValue);
        }
      }
      encryptionKey = encryptionKeyValue;
    }

    return encryptionKey;
  };

  return {
    async getItem(key: string, callback = noop) {
      try {
        let encryptedValue: string | null;
        let decryptedString: string | null;
        if (IS_WEB) {
          encryptedValue = await localforage.getItem(key);
        } else if (IS_IOS) {
          encryptedValue = await AsyncStorage.getItem(key);
        } else if (IS_ANDROID) {
          encryptedValue = await FileSystemStorage.getItem(key);
        } else {
          throw new Error(`unsupported OS: ${Platform.OS}`);
        }
        if (!ENCRYPTION_ENABLED) {
          // on mobile, we need to parse the unencrypted data to an object, as they are stored as strings.
          if (typeof encryptedValue !== 'object') {
            try {
              encryptedValue = JSON.parse(encryptedValue);
            } catch (error) {
              logError('Encrypted value is not a JSON string', error as Error);
            }
          }
          callback(null, encryptedValue);
          return encryptedValue;
        } else {
          if (encryptedValue === null) {
            // Migration: check for old unencrypted data stored under bare set ID
            const oldSetId = extractSetIdFromPrefixedKey(key);
            if (oldSetId) {
              try {
                let oldRaw: string | null = null;
                if (IS_WEB) {
                  oldRaw = await localforage.getItem(oldSetId);
                } else if (IS_IOS) {
                  oldRaw = await AsyncStorage.getItem(oldSetId);
                } else if (IS_ANDROID) {
                  oldRaw = await FileSystemStorage.getItem(oldSetId);
                }
                if (oldRaw !== null) {
                  try {
                    const parsed =
                      typeof oldRaw === 'string' ? JSON.parse(oldRaw) : oldRaw;
                    await this.setItem(key, parsed);
                    if (IS_WEB) {
                      await localforage.removeItem(oldSetId);
                    } else if (IS_IOS) {
                      await AsyncStorage.removeItem(oldSetId);
                    } else if (IS_ANDROID) {
                      await FileSystemStorage.removeItem(oldSetId);
                    }
                    callback(null, parsed);
                    return parsed;
                  } catch (migrationError) {
                    logError(
                      `Migration failed for set ${oldSetId}, wiping for re-download`,
                      migrationError,
                    );
                    if (IS_WEB) {
                      await localforage.removeItem(oldSetId);
                    } else if (IS_IOS) {
                      await AsyncStorage.removeItem(oldSetId);
                    } else if (IS_ANDROID) {
                      await FileSystemStorage.removeItem(oldSetId);
                    }
                  }
                }
              } catch (err) {
                logError(`Error during migration check for ${key}`, err);
              }
            }
          }
          if (encryptedValue) {
            // On web, encrypted data is always an ArrayBuffer. If the stored
            // value is not an ArrayBuffer it is old unencrypted data — return
            // it directly without attempting decryption.
            if (
              HAS_SUBTLE_CRYPTO_WEB &&
              !(encryptedValue instanceof ArrayBuffer)
            ) {
              const raw =
                typeof encryptedValue === 'string'
                  ? (() => {
                      try {
                        return JSON.parse(encryptedValue);
                      } catch {
                        return encryptedValue;
                      }
                    })()
                  : encryptedValue;
              callback(null, raw);
              return raw;
            }

            // On mobile, old unencrypted data is stored as plain JSON strings.
            // AES-GCM encrypted strings can never be valid JSON, so if JSON.parse
            // succeeds the value is unencrypted — return it directly to avoid
            // passing plain text to the native AES module which crashes natively
            // (EXC_BREAKPOINT) rather than throwing a catchable JS error.
            if (
              HAS_NATIVE_AES_GCM_CRYPTO &&
              typeof encryptedValue === 'string'
            ) {
              try {
                const raw = JSON.parse(encryptedValue);
                callback(null, raw);
                return raw;
              } catch {
                // Not valid JSON — must be encrypted, fall through to native decrypt
              }
            }

            try {
              const secretKey = await getEncryptionKey();

              if (HAS_SUBTLE_CRYPTO_WEB) {
                decryptedString = await aesGcmDecrypt(
                  encryptedValue,
                  secretKey,
                  key,
                );
              } else if (HAS_NATIVE_AES_GCM_CRYPTO) {
                decryptedString = await aesHelperMobile.decrypt(
                  encryptedValue,
                  secretKey,
                );
              }
            } catch (error) {
              // Try to read as plain unencrypted JSON (mobile migration fallback)
              try {
                if (typeof encryptedValue === 'string') {
                  const fallback = JSON.parse(encryptedValue);
                  callback(null, fallback);
                  return fallback;
                }
              } catch {}
              logError(`error during decryption of ${key}`, error);

              // Remove only this key — the value is unreadable but the
              // encryption key itself is fine. Wiping the key would make
              // every other stored value unreadable too.
              if (IS_WEB) {
                await localforage.removeItem(key);
              } else if (IS_IOS) {
                await AsyncStorage.removeItem(key);
              } else if (IS_ANDROID) {
                await FileSystemStorage.removeItem(key);
              }
              decryptedString = null;
            }
          } else {
            decryptedString = encryptedValue;
          }
          try {
            decryptedString = JSON.parse(decryptedString ?? 'null');
          } catch (error) {
            logError('Decrypted string was not an object', error as Error);
          }
          callback(null, decryptedString);
          return decryptedString;
        }
      } catch (error) {
        // Unexpected error reading a key — remove only this key and return null.
        // Do not wipe the encryption key as that would make every other stored
        // value unreadable and leave the app in an unusable state.
        logError(`error when loading encrypted data from ${key}`, error);
        try {
          if (IS_WEB) {
            await localforage.removeItem(key);
          } else if (IS_IOS) {
            await AsyncStorage.removeItem(key);
          } else if (IS_ANDROID) {
            await FileSystemStorage.removeItem(key);
          }
        } catch (removeError) {
          logError(`error when removing corrupt key ${key}`, removeError);
        }
        return null;
      }
    },

    async setItem(key: string, value: any, callback = noop) {
      try {
        let encryptedValue: string | ArrayBuffer = value;

        if (ENCRYPTION_ENABLED) {
          if (value !== null && value !== undefined) {
            value = JSON.stringify(value);
            const secretKey = await getEncryptionKey();
            if (HAS_SUBTLE_CRYPTO_WEB) {
              encryptedValue = await aesGcmEncrypt(value, secretKey, key);
            } else if (HAS_NATIVE_AES_GCM_CRYPTO) {
              encryptedValue = await aesHelperMobile.encrypt(value, secretKey);
            }
          }
        } else {
          if (typeof encryptedValue === 'object' && Platform.OS !== 'web') {
            encryptedValue = JSON.stringify(encryptedValue);
          }
        }
        if (
          typeof encryptedValue === 'boolean' ||
          typeof encryptedValue === 'number'
        ) {
          encryptedValue = JSON.stringify(encryptedValue);
        }

        if (IS_WEB) {
          await localforage.setItem(key, encryptedValue);
        } else if (IS_IOS) {
          await AsyncStorage.setItem(key, encryptedValue);
        } else if (IS_ANDROID) {
          await FileSystemStorage.setItem(key, encryptedValue);
        } else {
          throw new Error(`unsupported OS: ${Platform.OS}`);
        }

        callback(null);
      } catch (error) {
        callback(error);
        throw error;
      }
    },

    async removeItem(key: string, callback = noop) {
      try {
        if (IS_WEB) {
          await localforage.removeItem(key);
        } else if (IS_IOS) {
          await AsyncStorage.removeItem(key);
        } else if (IS_ANDROID) {
          await FileSystemStorage.removeItem(key);
        } else {
          throw new Error(`unsupported OS: ${Platform.OS}`);
        }
        callback(null);
      } catch (error) {
        callback(error);
        throw error;
      }
    },

    async listItems(callback = noop) {
      try {
        let result;
        if (IS_WEB) {
          result = await localforage.keys();
        } else if (IS_IOS) {
          result = await AsyncStorage.getAllKeys();
        } else if (IS_ANDROID) {
          result = await FileSystemStorage.getAllKeys();
        } else {
          throw new Error(`unsupported OS: ${Platform.OS}`);
        }
        callback(null, result);
        return result;
      } catch (error) {
        callback(error);
        throw error;
      }
    },

    async clear(callback = noop) {
      try {
        encryptionKey = null;
        if (IS_WEB) {
          await loadDeviceTokenDataWeb();
          window.localStorage.clear();
          await localforage.clear();
          await saveDeviceTokenDataWeb();
        } else if (IS_IOS) {
          await deleteItemAsync(encryptionKeyName);
          await AsyncStorage.clear();
        } else if (IS_ANDROID) {
          await deleteItemAsync(encryptionKeyName);
          await FileSystemStorage.clear();
        } else {
          throw new Error(`unsupported OS: ${Platform.OS}`);
        }

        callback(null);
      } catch (error) {
        callback(error);
        throw error;
      }
    },

    async clearAll() {
      encryptionKey = null;

      if (IS_WEB) {
        window.localStorage.clear();
        await localforage.clear();
      } else if (IS_IOS) {
        await deleteItemAsync(encryptionKeyName);
        await AsyncStorage.clear();
      } else if (IS_ANDROID) {
        await deleteItemAsync(encryptionKeyName);
        await FileSystemStorage.clear();
      }

      await this.destroyEncryptionKey();
    },
    /***
     * Destroys the currently used encryption key to make all persisted data unusable,
     * then generates a new encryption key for the next session
     *
     * @param callback
     * @returns {Promise<void>}
     */
    async destroyEncryptionKey(callback = noop) {
      encryptionKey = null;
      if (IS_WEB) {
        window.localStorage.removeItem(encryptionKeyName);
      } else {
        await deleteItemAsync(encryptionKeyName);
      }
      await getEncryptionKey();
      callback(null);
    },
  };
};

async function generateRandomEncryptionKey() {
  let encryptionKeyValue;
  // generating 64 cryptographic strong random bytes
  encryptionKeyValue = getRandomBytes(64);
  // convert those bytes to String
  encryptionKeyValue = String.fromCharCode.apply(null, encryptionKeyValue);
  // has the string with SHA256 to use it ase base64 AES key
  encryptionKeyValue = await digestStringAsync(
    CryptoDigestAlgorithm.SHA256,
    encryptionKeyValue,
    { encoding: CryptoEncoding.BASE64 },
  );
  return encryptionKeyValue;
}
